import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Clock, Info, LogOut, AlertCircle, Upload } from 'lucide-react';

const ReviewPendingPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Determine which documents are pending or rejected
  const policeVerificationPending = user?.policeVerificationStatus === 'pending';
  const policeVerificationRejected = user?.policeVerificationStatus === 'rejected';
  const kycPending = user?.kycStatus === 'pending';
  const kycRejected = user?.kycStatus === 'rejected';

  const getPendingDocuments = () => {
    const documents: string[] = [];
    if (policeVerificationPending || policeVerificationRejected) {
      documents.push('Police Verification');
    }
    if (kycPending || kycRejected) {
      documents.push('KYC Documents');
    }
    return documents;
  };

  const pendingDocs = getPendingDocuments();

  const handleReuploadPoliceVerification = () => {
    navigate('/complete-profile');
  };

  const handleReuploadKyc = () => {
    navigate('/kyc');
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full text-center">
        <div className="flex justify-center mb-6">
          <div className={`rounded-full p-4 ${
            policeVerificationRejected || kycRejected 
              ? 'bg-red-100' 
              : 'bg-amber-100'
          }`}>
            {policeVerificationRejected || kycRejected ? (
              <AlertCircle className="h-16 w-16 text-red-600" />
            ) : (
              <Clock className="h-16 w-16 text-amber-600" />
            )}
          </div>
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          {policeVerificationRejected || kycRejected 
            ? 'Document Review Rejected' 
            : 'Document Review Pending'}
        </h1>
        {policeVerificationRejected || kycRejected ? (
          <>
            <p className="text-lg text-gray-700 mb-2">
              Your {pendingDocs.join(' and ')} {pendingDocs.length === 1 ? 'has' : 'have'} been rejected.
            </p>
            <p className="text-sm text-gray-600 mb-6">
              Please review the rejection reason and upload new documents. You will not be able to access the app until your documents are approved.
            </p>
            {policeVerificationRejected && user?.policeVerificationRejectionReason && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 text-left">
                <p className="text-sm font-semibold text-red-900 mb-1">Rejection Reason:</p>
                <p className="text-sm text-red-700">{user.policeVerificationRejectionReason}</p>
              </div>
            )}
            {kycRejected && user?.kycRejectionReason && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 text-left">
                <p className="text-sm font-semibold text-red-900 mb-1">Rejection Reason:</p>
                <p className="text-sm text-red-700">{user.kycRejectionReason}</p>
              </div>
            )}
            <div className="space-y-3 mb-6">
              {policeVerificationRejected && (
                <button
                  onClick={handleReuploadPoliceVerification}
                  className="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  <Upload className="h-5 w-5 mr-2" />
                  Re-upload Police Verification
                </button>
              )}
              {kycRejected && (
                <button
                  onClick={handleReuploadKyc}
                  className="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  <Upload className="h-5 w-5 mr-2" />
                  Re-upload KYC Documents
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <p className="text-lg text-gray-700 mb-2">
              Your {pendingDocs.join(' and ')} {pendingDocs.length === 1 ? 'is' : 'are'} currently being reviewed by our admin team.
            </p>
            <p className="text-sm text-gray-600 mb-6">
              You will not be able to access the app until your documents are approved. We'll notify you once the review is complete.
            </p>
            <div className="bg-white border border-gray-300 rounded-lg p-4 mb-6 flex items-start">
              <Info className="h-5 w-5 text-black mt-0.5 mr-3 flex-shrink-0" />
              <p className="text-sm text-gray-700 text-left">
                Please wait for admin approval. You can check back later or contact support if you have any questions.
              </p>
            </div>
          </>
        )}
        <button
          onClick={handleLogout}
          className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
        >
          <LogOut className="h-5 w-5 mr-2" />
          Logout
        </button>
      </div>
    </div>
  );
};

export default ReviewPendingPage;

